import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Card from '../ui/Card/Card';
import Button from '../ui/Button/Button';
import { getIncubations, deleteIncubation } from '../../controllers/incubations';
import { useToast } from '../../contexts/ToastContext';
import { useUser } from '../../contexts/UserContext';
import './Incubations.css';
 

const Incubations = () => {
  const navigate = useNavigate();
  const { showError, showSuccess } = useToast();
  const { hasGlobalFunctionality, hasIncubationAccess, hasIncubationPermission, isDashboardAdmin } = useUser();
  const [incubations, setIncubations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deletingIncubationId, setDeletingIncubationId] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  useEffect(() => {
    fetchIncubations();
  }, []);

  const fetchIncubations = async () => {
    try {
      setLoading(true);
      const response = await getIncubations();
      
      if (response.data && response.data.success) {
        setIncubations(response.data.incubations || []);
      } else {
        console.error('Failed to fetch incubations:', response.data?.error);
        setIncubations([]);
      }
    } catch (error) {
      console.error('Error fetching incubations:', error);
      setIncubations([]);
    } finally {
      setLoading(false);
    }
  };

  const handleNewIncubationClick = () => {
    navigate('/create-incubation');
  };

  const handleRowClick = (incubationId) => {
    navigate('/incubation', { state: { incubationId: incubationId } });
  };

  const handleDeleteClick = (e, incubationId) => {
    e.stopPropagation(); // Prevent row click navigation
    
    if (confirmDeleteId === incubationId) {
      // Second click - actually delete
      handleDeleteIncubation(incubationId);
    } else {
      // First click - show confirmation
      setConfirmDeleteId(incubationId);
    }
  };

  const handleDeleteIncubation = async (incubationId) => {
    try {
      setDeletingIncubationId(incubationId);
      const response = await deleteIncubation({ incubation_id: incubationId });
      
      if (response.data && response.data.success) {
        showSuccess('Incubation deleted successfully!');
        // Refresh the incubations list
        await fetchIncubations();
        setConfirmDeleteId(null);
      } else {
        showError(response.data?.error || 'Failed to delete incubation');
      }
    } catch (error) {
      console.error('Error deleting incubation:', error);
      showError('Failed to delete incubation. Please try again.');
    } finally {
      setDeletingIncubationId(null);
    }
  };

  const handleCancelDelete = (e) => {
    e.stopPropagation();
    setConfirmDeleteId(null);
  };

  const canDeleteIncubation = (incubationId) => {
    return isDashboardAdmin() || hasIncubationPermission(incubationId, 'delete_incubations');
  };


  const getStatusClassName = (status) => {
    // Map database status values to CSS class names
    switch (status?.toLowerCase()) {
      case 'pre_launch':
        return 'prelaunch';
      case 'live':
      case 'active':
        return 'live';
      case 'completed':
        return 'completed';
      case 'archived':
        return 'archived';
      default:
        return 'prelaunch'; // Default to pre-launch styling
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: '2-digit', 
      year: 'numeric' 
    });
  };

  const getFilteredIncubations = () => {
    if (isDashboardAdmin() || hasGlobalFunctionality('manage_dashboard')) {
      return incubations;
    }
    return incubations.filter(incubation => hasIncubationAccess(incubation.id));
  };

  const filteredIncubations = getFilteredIncubations();

  return (
    <div className="incubations-container">
      <div className="incubations-content">
        {!loading && (
        <Card className={`chart-section incubations-card animate-in`}>
          <div className="card-header">
            <h2 className="chart-title">All Incubations</h2>
            {hasGlobalFunctionality('create_incubations') && (
              <Button variant="primary" size="small" onClick={handleNewIncubationClick}>
                <i className="fas fa-plus-circle"></i>
                New Incubation
              </Button>
            )}
          </div>
          <div className="incubations-table-container">
            <table className="incubations-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Start Date</th>
                  <th>End Date</th>
                  <th>Participants</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {filteredIncubations.length === 0 ? (
                  <tr>
                    <td colSpan="5" style={{ textAlign: 'center', padding: '20px', color: '#6b7280' }}>
                      No incubations found
                    </td>
                  </tr>
                ) : (
                  filteredIncubations.map((incubation, index) => (
                    <tr
                      key={incubation.id || index}
                      onClick={() => handleRowClick(incubation.id)}
                      className="incubation-row"
                    >
                      <td>{incubation.name}</td>
                      <td>{formatDate(incubation.start_date)}</td>
                      <td>{formatDate(incubation.end_date)}</td>
                      <td className="participants-cell">
                        <span className="participants-count">{incubation.participant_count ?? incubation.participants ?? 0}</span>
                      </td>
                      <td>
                        <span className={`status-badge ${getStatusClassName(incubation.status)}`}>
                          {incubation.status?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </Card>
        )}
      </div>
    </div>
  );
};

export default Incubations;